import {
  Button,
  Checkbox,
  FormPage,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@skylabs-digital/backoffice-kit';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiKeysApi } from '../api/apiKeys.api';
import { appsApi } from '../api/apps.api';

type ScopeMode = 'all' | 'specific';

export function ApiKeyNewPage() {
  const navigate = useNavigate();
  const { mutate: createKey, loading: creating } = apiKeysApi.useCreate();
  const { showSnackbar } = useSnackbar();
  const { data: apps } = appsApi.useList();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopeMode, setScopeMode] = useState<ScopeMode>('specific');
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');

  const isValid = name.trim().length > 0 && (scopeMode === 'all' || selectedApps.length > 0);

  const toggleApp = (id: string) =>
    setSelectedApps((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const scopes = scopeMode === 'all' ? ['*'] : selectedApps;
      // proto-kit's mutate resolves to the response envelope ({ success, data }),
      // not the bare entity — the secret lives under `data`.
      const response = (await createKey({
        name,
        description: description || undefined,
        scopes,
        expiresAt: expiresAt || undefined,
      } as never)) as { data?: { id?: string; key?: string } };
      const created = response?.data;

      showSnackbar({ message: 'API key created', variant: 'success' });

      if (created?.id && created?.key) {
        // One-shot reveal route — secret travels via location.state, never URL.
        navigate(`/api-keys/${created.id}/reveal`, {
          replace: true,
          state: { secret: created.key },
        });
      } else {
        navigate('/api-keys');
      }
    } catch {
      showSnackbar({ message: 'Failed to create API key', variant: 'error' });
    }
  };

  return (
    <FormPage
      title="Create API key"
      description="Issue a new global API key. The secret will be shown once — store it immediately."
      breadcrumbs={[{ label: 'API Keys', to: '/api-keys' }, { label: 'New' }]}
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Resuelto Backend"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional context for the key"
          />
        </div>

        <div className="space-y-2">
          <Label>Scopes *</Label>
          <Tabs value={scopeMode} onValueChange={(v) => setScopeMode(v as ScopeMode)}>
            <TabsList>
              <TabsTrigger value="all">All Apps (*)</TabsTrigger>
              <TabsTrigger value="specific">Specific Apps</TabsTrigger>
            </TabsList>
          </Tabs>
          {scopeMode === 'specific' ? (
            <div className="max-h-44 overflow-y-auto rounded-md border border-border p-3">
              {apps && apps.length > 0 ? (
                apps.map((app) => (
                  <label
                    key={app.id}
                    className="flex cursor-pointer items-center gap-2 py-1 text-sm"
                  >
                    <Checkbox
                      checked={selectedApps.includes(app.id)}
                      onCheckedChange={() => toggleApp(app.id)}
                    />
                    <span className="text-foreground">{app.name}</span>
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                      {app.id.slice(0, 8)}…
                    </span>
                  </label>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">No apps available</span>
              )}
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="expiresAt">Expires at</Label>
          <Input
            id="expiresAt"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Leave empty for no expiration.</p>
        </div>

        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={!isValid || creating}>
            {creating ? 'Creating…' : 'Create key'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/api-keys')}>
            Cancel
          </Button>
        </div>
      </form>
    </FormPage>
  );
}
